import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Users, Search, CheckSquare, MapPin, ChevronRight, ChevronLeft, Check } from 'lucide-react';

import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';

import {
  buscarEmpleados,
  listarInventario,
  asignarLote,
  type AsignarLotePayload,
  type TipoUbicacion,
  type InventarioItem,
} from '@/services/api/inventario.api';
import type { EmpleadoBusqueda } from '@/types/dominio';
import { getApiErrorMessage } from '@/services/api/client';
import { cn } from '@/lib/utils';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const PASOS = ['Empleado', 'Items', 'Confirmar'] as const;

export function EquiparEmpleadoDialog({ open, onOpenChange }: Props) {
  const qc = useQueryClient();
  const [paso, setPaso] = useState(0);

  // Paso 1 — empleado
  const [busquedaEmp, setBusquedaEmp] = useState('');
  const [empleadoSeleccionado, setEmpleadoSeleccionado] = useState<EmpleadoBusqueda | null>(null);

  // Paso 2 — items: Map<id, item> para conservar el objeto aunque cambie el filtro
  const [busquedaItem, setBusquedaItem] = useState('');
  const [seleccionados, setSeleccionados] = useState<Map<string, InventarioItem>>(new Map());

  // Paso 3 — ubicación
  const [tipoUbicacion, setTipoUbicacion] = useState<TipoUbicacion>('OFICINA');

  const { data: empleadosSugeridos = [], isFetching: buscandoEmp } = useQuery({
    queryKey: ['empleados-buscar', busquedaEmp],
    queryFn: () => buscarEmpleados(busquedaEmp),
    enabled: open && paso === 0,
  });

  const { data: itemsDisponibles, isFetching: cargandoItems } = useQuery({
    queryKey: ['inventario-items-bodega', busquedaItem],
    queryFn: () => listarInventario({ estado: 'EN_BODEGA', pageSize: 100, busqueda: busquedaItem }),
    enabled: open && paso === 1,
  });

  const mutation = useMutation({
    mutationFn: (payload: AsignarLotePayload) => asignarLote(payload),
    onSuccess: (data) => {
      toast.success(`${data.asignados.length} items asignados a ${data.empleado.nombres}`);
      qc.invalidateQueries({ queryKey: ['inventario-items'] });
      handleCerrar();
    },
    onError: (err) => {
      toast.error('No se pudo completar la asignación', {
        description: getApiErrorMessage(err),
      });
    },
  });

  function handleCerrar() {
    onOpenChange(false);
    setTimeout(() => {
      setPaso(0);
      setBusquedaEmp('');
      setEmpleadoSeleccionado(null);
      setBusquedaItem('');
      setSeleccionados(new Map());
      setTipoUbicacion('OFICINA');
    }, 200);
  }

  function toggleItem(item: InventarioItem) {
    setSeleccionados((prev) => {
      const next = new Map(prev);
      if (next.has(item.id_item_maestro)) next.delete(item.id_item_maestro);
      else next.set(item.id_item_maestro, item);
      return next;
    });
  }

  function handleConfirmar() {
    if (!empleadoSeleccionado || seleccionados.size === 0) return;
    mutation.mutate({
      id_empleado: empleadoSeleccionado.id_empleado,
      ids_items: Array.from(seleccionados.keys()),
      tipo_ubicacion: tipoUbicacion,
    });
  }

  const items = itemsDisponibles?.items ?? [];
  const itemsSeleccionados = Array.from(seleccionados.values());

  return (
    <Dialog open={open} onOpenChange={(o) => !mutation.isPending && (o ? undefined : handleCerrar())}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary-50 text-primary-700">
              <Users className="h-4 w-4" />
            </span>
            Equipar empleado
          </DialogTitle>
          <DialogDescription>
            Asigná varios elementos a una persona en una sola operación.
          </DialogDescription>
        </DialogHeader>

        {/* Stepper */}
        <div className="flex items-center gap-1 text-xs">
          {PASOS.map((label, i) => (
            <div key={label} className="flex items-center gap-1">
              <span
                className={cn(
                  'flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-semibold',
                  i < paso
                    ? 'bg-primary-600 text-white'
                    : i === paso
                      ? 'bg-primary-100 text-primary-700 ring-1 ring-primary-400'
                      : 'bg-muted text-muted-foreground',
                )}
              >
                {i + 1}
              </span>
              <span className={cn('hidden sm:inline', i === paso ? 'text-foreground font-medium' : 'text-muted-foreground')}>
                {label}
              </span>
              {i < PASOS.length - 1 && <ChevronRight className="h-3 w-3 text-muted-foreground" />}
            </div>
          ))}
        </div>

        {/* ── Paso 1: Empleado ── */}
        {paso === 0 && (
          <div className="grid gap-3">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Buscar por nombre o documento..."
                value={busquedaEmp}
                onChange={(e) => { setBusquedaEmp(e.target.value); setEmpleadoSeleccionado(null); }}
                className="pl-8"
                autoFocus
              />
            </div>

            {buscandoEmp && <p className="text-xs text-muted-foreground">Buscando...</p>}

            <div className="max-h-52 overflow-y-auto space-y-1">
              {empleadosSugeridos.map((emp) => (
                <button
                  key={emp.id_empleado}
                  type="button"
                  onClick={() => setEmpleadoSeleccionado(emp)}
                  className={cn(
                    'w-full flex items-center gap-3 rounded-md border px-3 py-2 text-left text-sm transition-colors',
                    empleadoSeleccionado?.id_empleado === emp.id_empleado
                      ? 'border-primary-400 bg-primary-50/60 ring-1 ring-primary-200'
                      : 'border-border hover:bg-muted/50',
                  )}
                >
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-600">
                    {emp.nombres.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-medium leading-none">{emp.nombres}</p>
                    <p className="text-xs text-muted-foreground">{emp.documento}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── Paso 2: Items ── */}
        {paso === 1 && (
          <div className="grid gap-3">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Filtrar por tipo o serial..."
                value={busquedaItem}
                onChange={(e) => setBusquedaItem(e.target.value)}
                className="pl-8"
                autoFocus
              />
            </div>

            {cargandoItems && <p className="text-xs text-muted-foreground">Cargando...</p>}

            <div className="max-h-64 overflow-y-auto space-y-1">
              {items.map((it) => (
                <label
                  key={it.id_item_maestro}
                  className={cn(
                    'flex cursor-pointer items-center gap-3 rounded-md border px-3 py-2 text-sm transition-colors',
                    seleccionados.has(it.id_item_maestro)
                      ? 'border-primary-400 bg-primary-50/60'
                      : 'border-border hover:bg-muted/50',
                  )}
                >
                  <button
                    type="button"
                    onClick={() => toggleItem(it)}
                    className={cn(
                      'flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors',
                      seleccionados.has(it.id_item_maestro)
                        ? 'border-primary-600 bg-primary-600 text-white'
                        : 'border-slate-300 bg-white',
                    )}
                  >
                    {seleccionados.has(it.id_item_maestro) && <Check className="h-3 w-3" />}
                  </button>
                  <div className="min-w-0 flex-1">
                    <p className="font-mono text-xs font-medium">{it.serial_global}</p>
                    <p className="truncate text-xs text-muted-foreground">
                      {it.tipo ?? '—'}{it.especificacion ? ` · ${it.especificacion}` : ''}
                    </p>
                  </div>
                  <Badge variant="outline" className="shrink-0 text-[10px]">{it.origen_datos}</Badge>
                </label>
              ))}
              {!cargandoItems && items.length === 0 && (
                <p className="py-4 text-center text-xs text-muted-foreground">Sin items disponibles en bodega</p>
              )}
            </div>

            {seleccionados.size > 0 && (
              <p className="text-xs text-primary-700 font-medium">{seleccionados.size} item(s) seleccionado(s) en total</p>
            )}
          </div>
        )}

        {/* ── Paso 3: Confirmar ── */}
        {paso === 2 && (
          <div className="grid gap-4">
            <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm">
              <p className="font-medium">{empleadoSeleccionado?.nombres}</p>
              <p className="text-xs text-muted-foreground">{empleadoSeleccionado?.documento}</p>
            </div>

            <div>
              <p className="mb-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Items a asignar ({itemsSeleccionados.length})
              </p>
              <div className="max-h-32 overflow-y-auto space-y-1">
                {itemsSeleccionados.map((it) => (
                  <div key={it.id_item_maestro} className="flex items-center gap-2 text-xs">
                    <CheckSquare className="h-3.5 w-3.5 text-primary-600 shrink-0" />
                    <span className="font-mono">{it.serial_global}</span>
                    <span className="text-muted-foreground truncate">{it.tipo ?? ''}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <p className="mb-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                <MapPin className="h-3 w-3" /> Ubicación
              </p>
              <div className="flex gap-2">
                {(['OFICINA', 'TELETRABAJO'] as TipoUbicacion[]).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setTipoUbicacion(t)}
                    className={cn(
                      'flex-1 rounded-md border px-3 py-2 text-sm font-medium transition-colors',
                      tipoUbicacion === t
                        ? 'border-primary-400 bg-primary-50/60 text-primary-700'
                        : 'border-border hover:bg-muted/50',
                    )}
                  >
                    {t === 'OFICINA' ? 'Oficina' : 'Teletrabajo'}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="flex-row justify-between sm:justify-between">
          <Button
            variant="outline"
            onClick={paso === 0 ? handleCerrar : () => setPaso((p) => p - 1)}
            disabled={mutation.isPending}
          >
            {paso === 0 ? 'Cancelar' : <><ChevronLeft className="h-4 w-4 mr-1" />Atrás</>}
          </Button>

          {paso < 2 ? (
            <Button
              onClick={() => setPaso((p) => p + 1)}
              disabled={
                (paso === 0 && !empleadoSeleccionado) ||
                (paso === 1 && seleccionados.size === 0)
              }
            >
              Siguiente <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={handleConfirmar} disabled={mutation.isPending}>
              {mutation.isPending ? (
                <><Loader2 className="animate-spin mr-1" />Asignando...</>
              ) : (
                `Asignar ${seleccionados.size} item(s)`
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
